import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackFormEvents } from '../../../../lib/analyticsEnhanced';
import { BottomSheetModal } from '../../../../components/v3/BottomSheetModal';
import {
  IOSFormGroup,
  IOSFormItem,
  IOSFormLabel,
  IOSFormToggle
} from '../ios/IOSFormGroup';

interface TaskImportanceEditorProps {
  isOpen: boolean;
  onClose: () => void;
  vital?: boolean;
  urgent?: boolean;
  fixed?: boolean;
  onSave: (data: { vital?: boolean; urgent?: boolean; fixed?: boolean }) => void | Promise<void>;
}

/**
 * 重要性设置弹窗组件
 * 保持原有 TaskForm 中重要性设置部分的完整功能
 */
export const TaskImportanceEditor: React.FC<TaskImportanceEditorProps> = ({
  isOpen,
  onClose,
  vital,
  urgent,
  fixed,
  onSave
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);

  // 本地状态
  const [localVital, setLocalVital] = useState<boolean>(vital || false);
  const [localUrgent, setLocalUrgent] = useState<boolean>(urgent || false);
  const [localFixed, setLocalFixed] = useState<boolean>(fixed || false);

  // 当弹窗打开时重置本地状态
  useEffect(() => {
    if (isOpen) {
      setLocalVital(vital || false);
      setLocalUrgent(urgent || false);
      setLocalFixed(fixed || false);
    }
  }, [isOpen, vital, urgent, fixed]);

  // 处理保存
  const handleSave = async () => {
    const changes = {
      vital: localVital,
      urgent: localUrgent,
      fixed: localFixed
    };

    // 记录编辑器保存事件
    trackFormEvents.editorSaved('importance_editor', changes, {
      is_vital: localVital,
      is_urgent: localUrgent,
      is_fixed: localFixed,
      importance_level: localVital && localUrgent ? 'critical' :
                      localVital ? 'important' :
                      localUrgent ? 'urgent' : 'normal'
    });

    await onSave(changes);
    onClose();
  };

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={onClose}
      title="重要性设置"
      onConfirm={handleSave}
      confirmText="保存"
      cancelText="取消"
      useGrayBackground={true}
      usePortal={true}
    >
      <div className="p-4">
        <IOSFormGroup>
          {/* 重要 */}
          <IOSFormItem className="flex items-center justify-between">
            <IOSFormLabel>{t('v3/tasks:form.vital')}</IOSFormLabel>
            <IOSFormToggle
              checked={localVital}
              onChange={setLocalVital}
            />
          </IOSFormItem>

          {/* 紧急 */}
          <IOSFormItem className="flex items-center justify-between">
            <IOSFormLabel>{t('v3/tasks:form.urgent')}</IOSFormLabel>
            <IOSFormToggle
              checked={localUrgent}
              onChange={setLocalUrgent}
            />
          </IOSFormItem>

          {/* 固定 */}
          <IOSFormItem className="flex items-center justify-between">
            <IOSFormLabel>{t('v3/tasks:form.fixed', '固定')}</IOSFormLabel>
            <IOSFormToggle
              checked={localFixed}
              onChange={setLocalFixed}
            />
          </IOSFormItem>
        </IOSFormGroup>

        {/* 艾森豪威尔矩阵说明 */}
        <div className="mt-4 p-3 bg-ios-gray6 dark:bg-ios-dark-gray5 rounded-ios-md">
          <div className="text-ios-subhead font-medium mb-2">{t('v3/tasks:importance.eisenhowerMatrix', '艾森豪威尔矩阵：')}</div>
          <div className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray space-y-1">
            <div>{t('v3/tasks:importance.importantUrgent', '• 重要且紧急：立即执行')}</div>
            <div>{t('v3/tasks:importance.importantNotUrgent', '• 重要不紧急：计划执行')}</div>
            <div>{t('v3/tasks:importance.notImportantUrgent', '• 不重要但紧急：委托执行')}</div>
            <div>{t('v3/tasks:importance.notImportantNotUrgent', '• 不重要不紧急：删除')}</div>
          </div>
        </div>
      </div>
    </BottomSheetModal>
  );
};
